import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { effect, type ReadonlySignal } from '@preact/signals-core';
import { property } from 'lit/decorators.js';

export class VirtualElementWrapper extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  private heightMeasureFrame: number | undefined;

  @property({ attribute: false })
  accessor rect!: {
    left$: ReadonlySignal<number | undefined>;
    top$: ReadonlySignal<number | undefined>;
    width$: ReadonlySignal<number | undefined>;
    height$: ReadonlySignal<number | undefined>;
  };

  @property({ attribute: false })
  accessor updateHeight!: (height: number) => void;
  @property({ attribute: false })
  accessor element!: HTMLElement;

  private readonly queueHeightMeasure = () => {
    if (this.heightMeasureFrame != null) {
      return;
    }
    this.heightMeasureFrame = requestAnimationFrame(() => {
      this.heightMeasureFrame = undefined;
      if (!this.element.isConnected) {
        return;
      }
      this.updateHeight(this.element.clientHeight);
    });
  };

  constructor() {
    super();
    // Give virtual rows/cells a non-visible absolute layout before they are
    // appended. On cold fullscreen/database loads the first browser paint can
    // happen before connectedCallback effects run; without this guard, newly
    // inserted wrappers can momentarily paint at normal-flow position 0.
    this.style.display = 'block';
    this.style.position = 'absolute';
    this.style.left = '-1000px';
    this.style.top = '-1000px';
    this.style.height = '0px';
    this.style.overflow = 'hidden';
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.style.position = 'absolute';
    this.disposables.add(
      effect(() => {
        this.style.left = `${this.rect.left$.value ?? -1000}px`;
        this.style.top = `${this.rect.top$.value ?? -1000}px`;
        if (this.rect.width$.value != null) {
          this.style.width = `${this.rect.width$.value}px`;
        }
        this.style.height = `${this.rect.height$.value ?? 0}px`;
        this.queueHeightMeasure();
      })
    );
    const resizeObserver = new ResizeObserver(() => {
      this.queueHeightMeasure();
    });
    resizeObserver.observe(this.element);
    this.disposables.add(() => {
      if (this.heightMeasureFrame != null) {
        cancelAnimationFrame(this.heightMeasureFrame);
        this.heightMeasureFrame = undefined;
      }
      resizeObserver.disconnect();
    });
  }
  override render() {
    return this.element;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'virtual-element-wrapper': VirtualElementWrapper;
  }
}
